import React, { useState } from 'react'
import { AriaTimeFieldProps, TimeValue, useLocale, useTimeField } from 'react-aria'
import { DateFieldState, TimeFieldStateOptions, useTimeFieldState } from 'react-stately'
import { parseTime } from '@internationalized/date'
import { typeValidator } from '@chilipiper/utils'
import invariant from 'tiny-invariant'
import styled from 'styled-components'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { PositioningProps } from '../../types'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { IconTooltip } from '../../feedback/icon-tooltip/IconTooltip'
import { Box, Grid, ScreenReaderOnly, VerticalDivider } from '../../../core-components'
import { DateFieldWrapper } from '../base-calendar/DateField'
import { FieldPopover } from '../internal/FieldPopover'
import { SelectList } from '../select-list'
import { TimeField } from './TimeField'

type Props = {
  'data-test-id'?: string
  errorMessage?: string
  isDisabled?: boolean
  label: React.ReactNode
  onChange: (value: string | undefined) => void
  /** ISO 8601 time string, for example "22:01:23.123" or "22:01:23" or "22:01" */
  value?: string
  variant?: 'regular' | 'nested' | 'ghost'
} & PositioningProps

export const TimePicker = ({
  value,
  onChange,
  errorMessage,
  variant = 'regular',
  label,
  isDisabled,
  'data-test-id': dataTestId,
  ...positioningProps
}: Props) => {
  invariant(
    typeValidator(TimePickerLabel)(label),
    'TimePicker: label must be a TimePicker.Label component'
  )
  validatePositioningProps(positioningProps, 'TimePicker')
  const { locale } = useLocale()
  const props: TimeFieldStateOptions<TimeValue> & AriaTimeFieldProps<TimeValue> = {
    value: value ? parseTime(value) : null,
    onChange: (time: TimeValue | null) => {
      if (time) {
        onChange(time.toString().slice(0, 5))
      } else {
        onChange(undefined)
      }
    },
    label,
    errorMessage,
    isDisabled,
    locale,
  }
  const state = useTimeFieldState(props)
  const ref = React.useRef<HTMLDivElement>(null)
  const { labelProps, descriptionProps, fieldProps, errorMessageProps } = useTimeField(
    props,
    state,
    ref
  )
  const [segmentToFocus, setSegmentToFocus] = useState<HTMLElement | undefined>(undefined)

  const handleDateFieldWrapperClick = () => {
    if (!isDisabled) {
      setIsPopoverOpen(true)
      segmentToFocus?.focus()
    }
  }
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const dayPeriodSegment = state.segments.find(segment => segment.type === 'dayPeriod')
  return (
    <SettingLabelContainer
      isRequired={false}
      label={label}
      labelElement='div'
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      {...positioningProps}
    >
      <FieldPopover
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        trigger={({ positionRef, popoverRef }) => {
          return (
            <StyledWrapper
              ref={positionRef}
              boxShadow={
                variant === 'regular' && !isDisabled && !errorMessage ? 'border/section' : undefined
              }
              borderRadius={radiusPerVariant[variant]}
            >
              <DateFieldWrapper
                px={variant === 'ghost' ? 2 : 5}
                data-open={isPopoverOpen}
                ref={ref}
                onClick={handleDateFieldWrapperClick}
                hasError={!!errorMessage}
                borderRadius={radiusPerVariant[variant]}
                variant={variant}
                data-test-id={dataTestId}
              >
                <TimeField
                  variant={variant}
                  state={state}
                  onSegmentToFocusChange={setSegmentToFocus}
                  popoverRef={popoverRef}
                  onOpenChange={setIsPopoverOpen}
                  fieldProps={fieldProps}
                  isDisabled={isDisabled}
                />
                {errorMessage && <IconTooltip ml={1} variant='error' content={errorMessage} />}
              </DateFieldWrapper>
            </StyledWrapper>
          )
        }}
      >
        <Grid
          borderRadius={12}
          w='342px'
          maxHeight='288px'
          overflow='hidden'
          bg='bg/dropdown'
          boxShadow='border/popover'
          gridAutoColumns='1fr 1px 1fr 1px 1fr'
          gridAutoFlow='column'
        >
          <HourOptionsList state={state} />
          <VerticalDivider />
          <MinuteOptionsList state={state} />
          {dayPeriodSegment && (
            <>
              <VerticalDivider />
              <SelectList
                INTERNAL_variant='nested'
                aria-label='AM/PM'
                value={(() => {
                  if (dayPeriodSegment.isPlaceholder) {
                    return undefined
                  }
                  if (dayPeriodSegment.value === 12) {
                    return 'PM'
                  }
                  return 'AM'
                })()}
                onChange={newValue => {
                  if (newValue === 'AM') {
                    state.setSegment('dayPeriod', 0)
                  } else if (newValue === 'PM') {
                    state.setSegment('dayPeriod', 12)
                  }
                }}
              >
                <SelectList.Item value='AM'>AM</SelectList.Item>
                <SelectList.Item value='PM'>PM</SelectList.Item>
              </SelectList>
            </>
          )}
        </Grid>
      </FieldPopover>

      <ScreenReaderOnly {...errorMessageProps}>{errorMessage}</ScreenReaderOnly>
    </SettingLabelContainer>
  )
}

const radiusPerVariant = {
  regular: 10,
  nested: undefined,
  ghost: 8,
} as const

const HourOptionsList = ({ state }: { state: DateFieldState }) => {
  const hourSegment = state.segments.find(segment => segment.type === 'hour')
  const dayPeriodSegment = state.segments.find(segment => segment.type === 'dayPeriod')
  const dayPeriod = dayPeriodSegment?.value ?? 0
  const hour = hourSegment?.isPlaceholder
    ? undefined
    : String((hourSegment?.value ?? 0) - dayPeriod)

  return (
    <SelectList
      INTERNAL_variant='nested'
      aria-label='hour'
      value={hour}
      onChange={newValue => {
        if (newValue) {
          state?.setSegment('hour', Number(newValue) + dayPeriod)
        }
      }}
    >
      {getRange((hourSegment?.minValue ?? 0) - dayPeriod, (hourSegment?.maxValue ?? 0) - dayPeriod)
        .map(String)
        .map(item => (
          <SelectList.Item key={item} value={item}>
            {item === '0' && dayPeriodSegment ? '12' : pad(item)}
          </SelectList.Item>
        ))}
    </SelectList>
  )
}

const MinuteOptionsList = ({ state }: { state: DateFieldState }) => {
  const minuteSegment = state.segments.find(segment => segment.type === 'minute')
  const minute = minuteSegment?.isPlaceholder ? undefined : String(minuteSegment?.value ?? 0)

  return (
    <SelectList
      INTERNAL_variant='nested'
      aria-label='minute'
      value={minute}
      onChange={newValue => {
        if (newValue) {
          state?.setSegment('minute', Number(newValue))
        }
      }}
    >
      {getRange(minuteSegment?.minValue ?? 0, minuteSegment?.maxValue ?? 0, 5)
        .map(String)
        .map(item => (
          <SelectList.Item key={item} value={item}>
            {pad(item)}
          </SelectList.Item>
        ))}
    </SelectList>
  )
}

const pad = (item: string) => (item.length === 1 ? `0${item}` : item)

const getRange = (start: number, end: number, step = 1) => {
  const length = Math.floor((end - start) / step) + 1
  return Array.from({ length }, (_, i) => start + i * step)
}

const StyledWrapper = styled(Box)`
  :hover::after {
    box-shadow: none;
  }
`
const TimePickerLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

TimePicker.Label = TimePickerLabel
